import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserProfile, updatePassword, toggleTwoFactor } from '../../../services/profileService';

const SecuritySection = () => {
  const { user } = useAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingPassword, setSavingPassword] = useState(false);
  const [toggling2FA, setToggling2FA] = useState(false);
  const [passwordChangedAt, setPasswordChangedAt] = useState(null);

  useEffect(() => {
    loadSecuritySettings();
  }, [user]);

  const loadSecuritySettings = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const profile = await getUserProfile(user?.id);
      
      if (profile) {
        setTwoFactorEnabled(profile?.twoFactorEnabled ?? false);
        setPasswordChangedAt(profile?.passwordChangedAt);
      }
    } catch (error) {
      console.error('Error loading security settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordChangedText = () => {
    if (!passwordChangedAt) return 'Never changed';
    
    const date = new Date(passwordChangedAt);
    const now = new Date();
    const diffMonths = Math.floor((now - date) / (1000 * 60 * 60 * 24 * 30));
    
    if (diffMonths === 0) return 'Changed recently';
    if (diffMonths === 1) return 'Changed 1 month ago';
    return `Changed ${diffMonths} months ago`;
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    if (passwordErrors?.[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    const errors = {};

    if (!passwordData?.currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    if (!passwordData?.newPassword) {
      errors.newPassword = "New password is required";
    } else if (passwordData?.newPassword?.length < 8) {
      errors.newPassword = "Password must be at least 8 characters";
    }

    if (!passwordData?.confirmPassword) {
      errors.confirmPassword = "Please confirm your new password";
    } else if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(errors)?.length > 0) {
      setPasswordErrors(errors);
      return;
    }

    try {
      setSavingPassword(true);
      const result = await updatePassword(passwordData?.currentPassword, passwordData?.newPassword);
      
      if (result?.success) {
        setShowPasswordForm(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
        loadSecuritySettings();
        alert('Password updated successfully!');
      } else {
        setPasswordErrors({ general: result?.error || 'Failed to update password' });
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setPasswordErrors({ general: 'Failed to update password. Please try again.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleTwoFactorToggle = async () => {
    if (!user?.id) return;

    if (twoFactorEnabled) {
      try {
        setToggling2FA(true);
        const result = await toggleTwoFactor(user?.id, false);
        
        if (result?.success) {
          setTwoFactorEnabled(false);
        } else {
          alert('Failed to disable 2FA. Please try again.');
        }
      } catch (error) {
        console.error('Error disabling 2FA:', error);
        alert('Failed to disable 2FA. Please try again.');
      } finally {
        setToggling2FA(false);
      }
    } else {
      setShowTwoFactorSetup(true);
    }
  };

  const handleTwoFactorSetup = async () => {
    if (!user?.id) return;

    try {
      setToggling2FA(true);
      const result = await toggleTwoFactor(user?.id, true);
      
      if (result?.success) {
        setTwoFactorEnabled(true);
        setShowTwoFactorSetup(false);
      } else {
        alert('Failed to enable 2FA. Please try again.');
      }
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      alert('Failed to enable 2FA. Please try again.');
    } finally {
      setToggling2FA(false);
    }
  };

  return (
    <div className="bg-card rounded-xl p-6 elevation-2">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-destructive rounded-lg">
          <Icon name="Lock" size={20} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Security Settings</h2>
          <p className="text-sm text-muted-foreground">Manage your account security and access</p>
        </div>
      </div>
      <div className="space-y-8">
        {/* Password Management */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">Password</h3>
          <div className="bg-muted/30 rounded-lg p-4">
            {!showPasswordForm ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Password</p>
                  <p className="text-xs text-muted-foreground">{getPasswordChangedText()}</p>
                </div>
                <Button variant="outline" onClick={() => setShowPasswordForm(true)}>
                  Change Password
                </Button>
              </div>
            ) : (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                {passwordErrors?.general && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                    {passwordErrors?.general}
                  </div>
                )}
                <Input
                  label="Current Password"
                  type="password"
                  name="currentPassword"
                  value={passwordData?.currentPassword}
                  onChange={handlePasswordChange}
                  error={passwordErrors?.currentPassword}
                  required
                />
                <Input
                  label="New Password"
                  type="password"
                  name="newPassword"
                  value={passwordData?.newPassword}
                  onChange={handlePasswordChange}
                  error={passwordErrors?.newPassword}
                  description="Must be at least 8 characters long"
                  required
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  name="confirmPassword"
                  value={passwordData?.confirmPassword}
                  onChange={handlePasswordChange}
                  error={passwordErrors?.confirmPassword}
                  required
                />
                <div className="flex space-x-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowPasswordForm(false)}
                    disabled={savingPassword}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="default" disabled={savingPassword}>
                    {savingPassword ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Two-Factor Authentication */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">Two-Factor Authentication</h3>
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-foreground">2FA Status</p>
                <p className="text-xs text-muted-foreground">
                  {twoFactorEnabled ? 'Two-factor authentication is enabled' : 'Add an extra layer of security'}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {twoFactorEnabled && (
                  <div className="flex items-center space-x-1 text-success">
                    <Icon name="Shield" size={16} />
                    <span className="text-xs font-medium">Enabled</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleTwoFactorToggle}
                  disabled={toggling2FA}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 ${
                    twoFactorEnabled ? 'bg-success' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            {twoFactorEnabled && (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                  <div className="flex items-center space-x-3">
                    <Icon name="Smartphone" size={20} className="text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Authenticator App</p>
                      <p className="text-xs text-muted-foreground">Google Authenticator</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    Manage
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Two-Factor Setup Modal */}
      {showTwoFactorSetup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 bg-success rounded-full">
                <Icon name="Shield" size={24} color="white" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Enable Two-Factor Authentication</h3>
            </div>
            <p className="text-muted-foreground mb-6">
              Two-factor authentication adds an extra layer of security to your account. You'll need to enter a code from your authenticator app when signing in.
            </p>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => setShowTwoFactorSetup(false)} className="flex-1" disabled={toggling2FA}>
                Cancel
              </Button>
              <Button variant="default" onClick={handleTwoFactorSetup} className="flex-1" disabled={toggling2FA}>
                {toggling2FA ? 'Enabling...' : 'Enable 2FA'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySection;